import { JSX, ReactNode, memo } from 'react'

import Head from 'next/head'
import { usePathname } from 'next/navigation'

import { BottomNavBar } from '~/components/Layout/BottomNavBar'
import { OrgSelector } from '~/components/Layout/OrgSelector'
import { PrivacyModeToggle } from '~/components/Layout/PrivacyModeToggle'
import { isTransactionPage } from '~/components/Layout/isTransactionPage'
import { PWAInstallBanner } from '~/components/PWAInstallBanner'
import { CommandMenuProvider } from '~/components/command-menu'
import { Sidebar } from '~/components/sidebar'
import { useAuth } from '~/context/AuthProvider'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useBanner } from '~/hooks/useBanner'
import { useHideSidebar } from '~/hooks/useHideSidebar'
import { useIsMobile } from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'

const LayoutComponent = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => {
  const isMobile = useIsMobile()
  const hideSidebar = useHideSidebar()
  const {
    onboarding: { isOnboarding },
  } = useAuth()
  const { user: authUser } = useAuthUser()
  const pathname = usePathname()
  const isOnTransactionPage = isTransactionPage(pathname)
  const showMobileNavigationUI =
    isMobile && authUser && !isOnTransactionPage && !isOnboarding

  const isSplitsConnectPage = pathname.startsWith('/connect')
  // Public invoice pages (e.g., /invoices/abc123) shouldn't show mobile nav
  const isPublicInvoicePage =
    pathname.startsWith('/invoices/') &&
    !pathname.startsWith('/invoices/create')

  const { showBanner, banner: Banner } = useBanner()

  return (
    <>
      {showBanner && Banner}
      {authUser && !isOnboarding && <PWAInstallBanner />}
      <Head>
        <meta name="theme-color" content={isMobile ? '#000000' : '#ffffff'} />
      </Head>
      {showMobileNavigationUI &&
        !isSplitsConnectPage &&
        !isPublicInvoicePage && (
          <OrgSelector trailing={<PrivacyModeToggle className="ml-1" />} />
        )}

      <CommandMenuProvider>
        <main
          className={cn('flex bg-white text-primary dark:bg-black', {
            'md:pt-10': showBanner,
          })}
        >
          {!hideSidebar && <Sidebar />}
          <div className="relative min-w-0 flex-1 overflow-x-clip">
            {children}
          </div>
          {showMobileNavigationUI &&
            !isSplitsConnectPage &&
            !isPublicInvoicePage && <BottomNavBar />}
        </main>
      </CommandMenuProvider>
    </>
  )
}

export const Layout = memo(LayoutComponent)
